/**
 * TitleScene — the calm front door. Boot -> Preload -> *Title* -> (Town | NewGame).
 *
 * D16 (multi-slot saves):
 *   • "New game" ALWAYS starts a fresh slot (NewGameScene -> createNew mints a new
 *     slotId every time) — it NEVER overwrites an existing save.
 *   • "Continue" opens a SAVE PICKER: a scrollable list of every save, each row
 *     labeled with the trainer's name + a warm last-played stamp ("today 8:14pm" /
 *     "Jun 19") + a glanceable progress summary ("Lv 14 · 23 caught"). Tap a row to
 *     resume that world (loadSlot -> the soft-capped welcome-back -> Town); each row
 *     has a gentle delete with a confirm. With no saves, Continue is hidden.
 *
 * Touch-first + responsive throughout: every control is a big tap target from the UI
 * kit, placed via Layout so it adapts phone <-> desktop and relayouts on resize. The
 * row display strings are formatted by the pure ./savePicker module (unit-tested).
 */
import Phaser from 'phaser';
import { GameSession } from '@/session';
import type { SaveSummary } from '@/session';
import { COLORS, CSS, FONT, RADIUS, SPACING, TOUCH_TARGET } from './ui/theme.js';
import { Button } from './ui/Button.js';
import { Menu } from './ui/Menu.js';
import { ScrollList } from './ui/ScrollList.js';
import { FocusRing } from './ui/FocusRing.js';
import { moveGrid } from './ui/gridNav.js';
import { domTextFieldFocused } from './ui/keyboardGate.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { REGISTRY_HAS_SAVE } from './PreloadScene.js';
import { setSession, sessionDeps } from './session.js';
import { saveRowLabels, deleteConfirmText } from './savePicker.js';
import { titleTopOnly } from './titleInput.js';

/** Which front-door view is on screen. */
type View = 'menu' | 'picker';

/** One picker row's live handles (so a delete can dim/splice it out). */
interface SaveRow {
  readonly summary: SaveSummary;
  readonly container: Phaser.GameObjects.Container;
}

const ROW_HEIGHT = 84;

export class TitleScene extends Phaser.Scene {
  private layout!: Layout;
  private title?: Phaser.GameObjects.Text;
  private subtitle?: Phaser.GameObjects.Text;
  /** The classic ▶-cursor option menu (Continue / New game). */
  private menu?: Menu;
  private reduced = false;
  /** Set while a resume/picker action is in flight, so we never double-fire. */
  private busy = false;
  private view: View = 'menu';

  /** Picker view objects (built lazily on Continue, torn down on Back). */
  private picker?: Phaser.GameObjects.Container;
  private pickerList?: ScrollList;
  private pickerRows: SaveRow[] = [];
  /** The resolved saves, cached so a resize can rebuild the rows at the new list size. */
  private pickerSaves: readonly SaveSummary[] = [];
  private pickerBack?: Button;
  private pickerEmpty?: Phaser.GameObjects.Text;
  /** A simple in-scene confirm overlay (Delete <name>?). */
  private confirm?: Phaser.GameObjects.Container;
  /** The confirm's Delete action, so the keyboard (Enter) can fire it. */
  private confirmAction?: () => void;

  // ── picker keyboard nav ─────────────────────────────────────────────────────
  /** Keyboard cursor over the picker rows; -1 until the player uses a key. */
  private pickerCursor = -1;
  private pickerRowWidth = 0;
  private pickerListViewH = 0;
  private focusRing?: FocusRing;
  private keyHandler?: (e: KeyboardEvent) => void;

  constructor() {
    super('Title');
  }

  create(): void {
    // Phaser REUSES this scene instance, so create() re-runs if we ever return to the
    // Title. The picker arrays/handles and the `busy`/`view` guards survive between
    // visits — reset them so a re-entry never starts latched-busy or with a stale picker.
    this.resetState();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);

    // The front-door MENU must be TOP-ONLY so a tap on "Continue" resolves to exactly
    // that one row. The save picker view opts into pass-through (`input.topOnly = false`);
    // without re-asserting top-only here, a prior visit's pass-through would linger and let
    // a tap near the Continue/New-game row boundary co-fire BOTH options — dropping the
    // player into character creation. applyHitMode pins the flag to the current view on
    // entry, on every view swap, and on re-entry, so the two views' modes never leak.
    this.applyHitMode();

    const hasSave = (this.registry.get(REGISTRY_HAS_SAVE) as boolean | undefined) ?? false;
    this.reduced = isReducedMotion();

    this.title = this.add
      .text(0, 0, 'Parallel Routes', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.title}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.subtitle = this.add
      .text(0, 0, hasSave ? 'the town has missed you' : 'a small, lively region awaits', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    // Continue opens the save picker; New game always starts a fresh slot. Continue
    // is only offered when Preload found at least one save (re-checked live in the
    // picker — an empty picker shows a gentle "no saves yet" and a Back).
    const items = hasSave
      ? [
          { label: 'Continue', onSelect: () => this.openPicker() },
          { label: 'New game', onSelect: () => this.toNewGame() },
        ]
      : [{ label: 'New game', onSelect: () => this.toNewGame() }];

    this.menu = new Menu(this, 0, 0, {
      items,
      width: 240,
      reducedMotion: this.reduced,
    });
    this.menu.focus();

    this.focusRing = new FocusRing(this, { depth: 50 });

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.setupKeyboard();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);
  }

  /**
   * Save-picker keyboard nav (the front-door option Menu handles its own keys). In the
   * picker: up/down move the row highlight, Enter resumes, Delete/Backspace opens the
   * row's delete confirm, Esc backs out (or dismisses an open confirm). Ignored while a
   * DOM text field is focused. Cleaned up on SHUTDOWN.
   */
  private setupKeyboard(): void {
    this.keyHandler = (e: KeyboardEvent): void => {
      if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) return;
      // The delete-confirm modal captures keys: Enter deletes, Esc keeps.
      if (this.confirm) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.confirmAction?.();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.dismissConfirm();
        }
        return;
      }
      // Only the picker view is keyboard-driven here; the option Menu owns its own keys.
      if (this.view !== 'picker' || this.busy) return;
      const n = this.pickerRows.length;
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
          if (n > 0) {
            e.preventDefault();
            this.setPickerCursor(
              this.pickerCursor < 0 ? 0 : moveGrid(this.pickerCursor, 'up', n, 1),
            );
          }
          break;
        case 'ArrowDown':
        case 's':
          if (n > 0) {
            e.preventDefault();
            this.setPickerCursor(
              this.pickerCursor < 0 ? 0 : moveGrid(this.pickerCursor, 'down', n, 1),
            );
          }
          break;
        case 'Enter':
        case ' ':
          if (this.pickerCursor >= 0 && this.pickerSaves[this.pickerCursor]) {
            e.preventDefault();
            void this.resume(this.pickerSaves[this.pickerCursor]!);
          }
          break;
        case 'Delete':
        case 'Backspace':
          if (this.pickerCursor >= 0 && this.pickerSaves[this.pickerCursor]) {
            e.preventDefault();
            this.askDelete(this.pickerSaves[this.pickerCursor]!);
          }
          break;
        case 'Escape':
          e.preventDefault();
          this.closePicker();
          break;
        default:
          break;
      }
    };
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.keyHandler);
  }

  /** Seat the keyboard cursor on a picker row: scroll into view + draw the focus ring. */
  private setPickerCursor(index: number): void {
    const n = this.pickerRows.length;
    if (n === 0 || !this.pickerList) {
      this.focusRing?.hide();
      return;
    }
    this.pickerCursor = Math.max(0, Math.min(n - 1, index));
    const row = this.pickerRows[this.pickerCursor];
    if (!row) return;
    const cy = row.container.y;
    const top = cy - ROW_HEIGHT / 2;
    const bottom = cy + ROW_HEIGHT / 2;
    const off = this.pickerList.scrollOffset;
    const pad = SPACING.sm;
    if (top < off) this.pickerList.scrollTo(top - pad);
    else if (bottom > off + this.pickerListViewH) {
      this.pickerList.scrollTo(bottom - this.pickerListViewH + pad);
    }
    const worldX = this.pickerList.x;
    const worldY = this.pickerList.y + cy - this.pickerListViewH / 2 - this.pickerList.scrollOffset;
    this.focusRing?.show(worldX, worldY, this.pickerRowWidth, ROW_HEIGHT);
    this.focusRing?.bringToTop();
  }

  /**
   * Drop every reference a prior visit's create() populated so re-entry starts clean.
   * Display objects were torn down by the previous SHUTDOWN (teardown()); this clears the
   * arrays/flags/handles so nothing stacks and the busy guard releases. Safe on a first
   * entry (everything already empty / false).
   */
  private resetState(): void {
    this.title = undefined;
    this.subtitle = undefined;
    this.menu = undefined;
    this.busy = false;
    this.view = 'menu';
    this.picker = undefined;
    this.pickerList = undefined;
    this.pickerRows = [];
    this.pickerSaves = [];
    this.pickerBack = undefined;
    this.pickerEmpty = undefined;
    this.confirm = undefined;
    this.confirmAction = undefined;
    this.pickerCursor = -1;
    this.focusRing = undefined;
    this.keyHandler = undefined;
  }

  /**
   * Pin `scene.input.topOnly` to whatever the CURRENT view needs (pure rule in
   * ./titleInput): the MENU view is top-only (each tap hits exactly one option, so
   * Continue is never co-fired with New game), the PICKER view passes through (belt-and-
   * suspenders so every save row + Back stays tappable). Called on entry, on every view
   * swap, and on re-entry — so the picker's pass-through can never leave the menu stuck in
   * a mode that co-fires the two options.
   */
  private applyHitMode(): void {
    this.input.topOnly = titleTopOnly(this.view);
  }

  /**
   * SHUTDOWN handler — Phaser reuses this scene instance, so leave nothing behind: kill
   * tweens, destroy the title/menu/picker/confirm (containers cascade to their children),
   * then drop the references.
   */
  private teardown(): void {
    this.tweens.killAll();
    if (this.keyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.keyHandler = undefined;
    this.focusRing?.destroy();
    this.focusRing = undefined;
    this.confirmAction = undefined;
    this.title?.destroy();
    this.subtitle?.destroy();
    this.menu?.destroy();
    this.picker?.destroy(true);
    this.confirm?.destroy(true);
    this.title = undefined;
    this.subtitle = undefined;
    this.menu = undefined;
    this.picker = undefined;
    this.pickerList = undefined;
    this.pickerRows = [];
    this.pickerSaves = [];
    this.pickerBack = undefined;
    this.pickerEmpty = undefined;
    this.confirm = undefined;
  }

  private toNewGame(): void {
    if (this.busy) return;
    goTo(this, 'NewGame');
  }

  // ── the save picker (Continue) ──────────────────────────────────────────────────

  /** Swap the option menu for the scrollable save picker, loading the slot list. */
  private async openPicker(): Promise<void> {
    if (this.busy || this.view !== 'menu') return;
    this.view = 'picker';
    // The picker view passes through so every save row + Back stays reliably tappable.
    // applyHitMode owns the flag explicitly (ScrollList no longer touches topOnly), so the
    // mode is correct regardless of the list's build order.
    this.applyHitMode();
    this.menu?.blur();
    this.menu?.setVisible(false);
    this.subtitle?.setText('which world shall we step into?');

    const container = this.add.container(0, 0);
    this.picker = container;

    const safe = this.layout.safe;
    const listWidth = Math.min(460, safe.width - SPACING.lg * 2);
    const listHeight = Math.min(safe.height * 0.52, 380);

    const list = new ScrollList(this, 0, 0, { width: listWidth, height: listHeight });
    container.add(list);
    this.pickerList = list;

    this.pickerBack = new Button(this, 0, 0, {
      label: 'Back',
      variant: 'ghost',
      width: 200,
      reducedMotion: this.reduced,
      onClick: () => this.closePicker(),
    });
    container.add(this.pickerBack);

    // A quiet "loading" line while listSaves resolves (local IndexedDB + server).
    this.pickerEmpty = this.add
      .text(0, 0, 'looking for your saves…', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);
    container.add(this.pickerEmpty);

    this.relayout();

    const saves = await GameSession.listSaves(sessionDeps());
    // The player may have backed out while the list resolved.
    if (this.view !== 'picker' || !this.scene.isActive()) return;

    this.buildRows(saves, listWidth);
  }

  /** Materialize one row per save into the scroll list (newest first; already sorted). */
  private buildRows(saves: readonly SaveSummary[], listWidth: number): void {
    this.pickerSaves = saves;
    this.pickerRows = [];
    this.pickerList?.content.removeAll(true);

    if (saves.length === 0) {
      this.pickerEmpty?.setText('no saves yet — start a new game');
      this.subtitle?.setText('the town has missed you');
      this.pickerList?.setContentHeight(0);
      this.pickerCursor = -1;
      this.focusRing?.hide();
      return;
    }
    this.pickerEmpty?.setVisible(false);

    const rowWidth = listWidth - SPACING.md;
    this.pickerRowWidth = rowWidth;
    // ScrollList content is TOP-anchored (applyOffset maps content-local y=0 to the
    // viewport top), so rows start at a small top padding — NOT center-anchored at
    // -height/2, which pushed them a half-viewport above the clip (picker rendered empty).
    let cursorY = SPACING.sm;
    for (const save of saves) {
      const row = this.makeRow(save, rowWidth);
      row.container.y = cursorY + ROW_HEIGHT / 2;
      this.pickerList?.addItem(row.container, ROW_HEIGHT + SPACING.sm);
      cursorY += ROW_HEIGHT + SPACING.sm;
      this.pickerRows.push(row);
    }
    this.pickerList?.scrollTo(0);
    // Re-seat the keyboard cursor if it was already active (e.g. after a delete rebuild).
    if (this.pickerCursor >= 0) this.setPickerCursor(this.pickerCursor);
  }

  /** Build a single save row: a tappable card (resume) + a small delete affordance. */
  private makeRow(save: SaveSummary, width: number): SaveRow {
    const labels = saveRowLabels(save);
    const container = this.add.container(0, 0);

    // The card body — a cream box; tapping it (outside the delete) resumes the slot.
    const gfx = this.add.graphics();
    const h = ROW_HEIGHT;
    const r = RADIUS.md;
    gfx.fillStyle(COLORS.boxFill, 1);
    gfx.fillRoundedRect(-width / 2, -h / 2, width, h, r);
    gfx.lineStyle(2, COLORS.boxBorder, 1);
    gfx.strokeRoundedRect(-width / 2, -h / 2, width, h, r);
    container.add(gfx);

    const padX = SPACING.md;
    const name = this.add
      .text(-width / 2 + padX, -h / 2 + SPACING.sm + 2, labels.name, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.md}px`,
        color: CSS.boxText,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);
    const when = this.add
      .text(width / 2 - padX, -h / 2 + SPACING.sm + 4, labels.when, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.boxTextMuted,
      })
      .setOrigin(1, 0);
    const summary = this.add
      .text(-width / 2 + padX, h / 2 - SPACING.sm - 2, labels.summary, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.boxTextMuted,
      })
      .setOrigin(0, 1);
    container.add([name, when, summary]);

    // Whole-card tap target to resume — sized to leave the delete button room.
    const deleteW = TOUCH_TARGET;
    const tapZone = this.add
      .zone(-deleteW / 2, 0, width - deleteW, h)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    // Resume on TAP (POINTER_UP); a drag-scroll of the picker no-ops here so dragging a
    // full-bleed save row scrolls the list instead of resuming that save.
    tapZone.on(Phaser.Input.Events.POINTER_UP, () => {
      if (!this.pickerList?.wasDragging()) this.resume(save);
    });
    container.add(tapZone);

    // A gentle delete affordance on the right edge (its own tap target + confirm).
    const del = this.add
      .text(width / 2 - padX, h / 2 - SPACING.sm - 2, '🗑', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.boxTextMuted,
      })
      .setOrigin(1, 1);
    const delZone = this.add
      .zone(width / 2 - TOUCH_TARGET / 2, h / 2 - TOUCH_TARGET / 2, TOUCH_TARGET, TOUCH_TARGET)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    delZone.on(Phaser.Input.Events.POINTER_UP, () => {
      if (!this.pickerList?.wasDragging()) this.askDelete(save);
    });
    container.add([del, delZone]);

    return { summary: save, container };
  }

  /** Resume a chosen slot: load -> setSession -> welcome-back (or straight to Town). */
  private async resume(save: SaveSummary): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.subtitle?.setText('waking the town…');
    this.disablePicker(true);

    // loadSlot is non-throwing by contract; the .catch is a final belt-and-suspenders.
    const session = await GameSession.loadSlot(save.slotId, sessionDeps()).catch(() => null);

    if (!session) {
      // The slot vanished (deleted elsewhere) — refresh the list rather than crash.
      this.busy = false;
      this.subtitle?.setText('that save could not be found — pick another');
      this.disablePicker(false);
      const saves = await GameSession.listSaves(sessionDeps());
      if (this.view === 'picker' && this.scene.isActive()) {
        const listWidth = Math.min(460, this.layout.safe.width - SPACING.lg * 2);
        this.buildRows(saves, listWidth);
        this.relayout();
      }
      return;
    }

    // loadSlot already ran the soft-capped idle-replay + persisted inside the data
    // layer (its welcome-back is folded into the resumed world); step gently into Town.
    setSession(this, session);
    goTo(this, 'Town');
  }

  private disablePicker(disabled: boolean): void {
    this.pickerBack?.setDisabled(disabled);
    for (const row of this.pickerRows) {
      row.container.setAlpha(disabled ? 0.5 : 1);
      row.container.list.forEach((o) => {
        const z = o as Phaser.GameObjects.Zone;
        if (z.input) z.input.enabled = !disabled;
      });
    }
  }

  /** Tear down the picker and return to the front-door option menu. */
  private closePicker(): void {
    if (this.busy) return;
    this.view = 'menu';
    // Back at the front door: re-assert TOP-ONLY. The picker view ran in pass-through mode,
    // which would let a Continue tap co-fire New game. Restoring it here (and on re-entry,
    // via create()) is what makes Continue reliable on the 2nd+ open.
    this.applyHitMode();
    this.subtitle?.setText('the town has missed you');
    this.picker?.destroy(true);
    this.picker = undefined;
    this.pickerList = undefined;
    this.pickerRows = [];
    this.pickerSaves = [];
    this.pickerBack = undefined;
    this.pickerEmpty = undefined;
    this.pickerCursor = -1;
    this.focusRing?.hide();
    this.menu?.setVisible(true);
    this.menu?.focus();
    this.relayout();
  }

  // ── delete confirm ────────────────────────────────────────────────────────────

  private askDelete(save: SaveSummary): void {
    if (this.busy || this.confirm) return;
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;

    const overlay = this.add.container(0, 0).setDepth(100);
    this.confirm = overlay;
    this.confirmAction = () => void this.doDelete(save);

    // A scrim that swallows taps behind the dialog.
    const scrim = this.add
      .rectangle(cx, cy, safe.width + SPACING.xxl, safe.height + SPACING.xxl, COLORS.scrim, 0.6)
      .setInteractive();
    overlay.add(scrim);

    const boxW = Math.min(380, safe.width - SPACING.lg * 2);

    // MEASURE-TO-CONTENT: build the prompt first and read its wrapped height, so a long
    // trainer name that wraps to several lines on a narrow phone never sits on the
    // Keep/Delete buttons. The box height is sized to the measured text + the button row.
    const prompt = this.add
      .text(0, 0, deleteConfirmText(save), {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.boxText,
        align: 'center',
        wordWrap: { width: boxW - SPACING.lg * 2 },
      })
      .setOrigin(0.5, 0);
    const btnH = Math.max(TOUCH_TARGET, FONT.size.md + SPACING.lg);
    const boxH = SPACING.lg + prompt.height + SPACING.lg + btnH + SPACING.lg;

    const box = this.add.graphics();
    box.fillStyle(COLORS.boxFill, 1);
    box.fillRoundedRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH, RADIUS.md);
    box.lineStyle(3, COLORS.boxBorder, 1);
    box.strokeRoundedRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH, RADIUS.md);
    overlay.add(box);

    prompt.setPosition(cx, cy - boxH / 2 + SPACING.lg);
    overlay.add(prompt);

    const btnCy = cy + boxH / 2 - SPACING.lg - btnH / 2;
    const cancel = new Button(this, cx - boxW / 4, btnCy, {
      label: 'Keep',
      variant: 'ghost',
      width: boxW / 2 - SPACING.md,
      reducedMotion: this.reduced,
      onClick: () => this.dismissConfirm(),
    });
    const confirmBtn = new Button(this, cx + boxW / 4, btnCy, {
      label: 'Delete',
      variant: 'warm',
      width: boxW / 2 - SPACING.md,
      reducedMotion: this.reduced,
      onClick: () => void this.doDelete(save),
    });
    overlay.add([cancel, confirmBtn]);
  }

  private dismissConfirm(): void {
    this.confirm?.destroy(true);
    this.confirm = undefined;
    this.confirmAction = undefined;
  }

  /** Delete the slot (local + server), splice its row out, refresh the picker. */
  private async doDelete(save: SaveSummary): Promise<void> {
    this.dismissConfirm();
    await GameSession.deleteSlot(save.slotId, sessionDeps());
    if (!this.scene.isActive() || this.view !== 'picker') return;

    const saves = await GameSession.listSaves(sessionDeps());
    const listWidth = Math.min(460, this.layout.safe.width - SPACING.lg * 2);
    this.buildRows(saves, listWidth);
    this.relayout();
  }

  // ── layout ────────────────────────────────────────────────────────────────────

  private relayout(): void {
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;
    const titleY = cy - SPACING.xxl * 1.5;
    this.title?.setPosition(cx, titleY);
    // Seat the subtitle BELOW the title's measured bounding box (both are origin-0.5):
    // the big title font is ~44px tall, so a fixed near-coincident offset let the two
    // bounding boxes touch. Drop the subtitle to the title's bottom edge + a clear gap.
    const titleBottom = titleY + (this.title?.height ?? FONT.size.title) / 2;
    const subHalf = (this.subtitle?.height ?? FONT.size.sm) / 2;
    this.subtitle?.setPosition(cx, titleBottom + SPACING.md + subHalf);

    if (this.view === 'menu') {
      this.menu?.setPosition(cx, cy + (this.menu?.boxHeight ?? 0) / 2);
      return;
    }

    if (this.view === 'picker' && this.picker) {
      const safe = this.layout.safe;
      // Anchor the picker BELOW the subtitle (not centered on cy) so the big title +
      // subtitle never overlap the scrolling save rows on a tall narrow phone — and the
      // list + Back button always fit between the subtitle and the safe bottom edge.
      const subBottom = (this.subtitle?.y ?? cy) + (this.subtitle?.height ?? FONT.size.sm) / 2;
      const listTop = subBottom + SPACING.xl;
      const backH = this.pickerBack?.buttonHeight ?? 48;
      const bottomBound = safe.y + safe.height - SPACING.lg - backH - SPACING.lg;
      const listHeight = Math.min(Math.max(120, bottomBound - listTop), safe.height * 0.52, 380);
      const listCenterY = listTop + listHeight / 2;
      const listWidth = Math.min(460, safe.width - SPACING.lg * 2);
      this.pickerListViewH = listHeight;
      this.pickerList?.setPosition(cx, listCenterY);
      this.pickerList?.resize(listWidth, listHeight);
      this.pickerEmpty?.setPosition(cx, listCenterY);
      this.pickerBack?.setPosition(cx, listCenterY + listHeight / 2 + SPACING.xl);
      // Rows are positioned relative to the list height, so a resize (orientation change)
      // must re-lay them at the new size. Only once they've been built (saves resolved).
      if (this.pickerRows.length > 0) this.buildRows(this.pickerSaves, listWidth);
      // Re-seat the keyboard focus ring on the (moved) focused row.
      if (this.pickerCursor >= 0) this.setPickerCursor(this.pickerCursor);
    }
  }
}
